<template>
    <div class="flex-1">
        <skeleton v-if="purchaseAgreementInput === undefined" />
        <div v-else-if="purchaseAgreementInput === null">
            Whoops! We could not find that plan at this facility.
        </div>
        <purchase-agreement-language
            v-else
            :purchase-agreement-input="purchaseAgreementInput"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, onServerPrefetch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import Skeleton from 'primevue/skeleton'
import { useCurrentDate } from '~/composables/use-current-date'
import PurchaseAgreementLanguage from '~/components/PurchaseContract/Agreement/PurchaseAgreementLanguage.vue'

const props = defineProps<{ planId: string }>()
const planId = computed(() => props.planId)
const activeFacility = useActiveFacility()
const activeFacilityId = computed(() => activeFacility.value.id)

const currentDate = useCurrentDate()
const reactiveDate = computed(() => currentDate.value.getDate())
const query = useRpQuery()
const { data, suspense } = useQuery({
    queryKey: [
        'PurchaseAgreementWrapperQuery',
        planId,
        activeFacilityId,
        reactiveDate
    ],
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query PurchaseAgreementWrapperQuery($planId: ID!) {
                    plan(id: $planId) {
                        terms {
                            language
                        }
                    }
                }
            `),
            {
                planId: planId.value
            }
        )
})

const purchaseAgreementInput = computed(() => {
    if (data.value === undefined) {
        return undefined
    }

    if (data.value.plan === null) {
        return null
    }

    return {
        planId: planId.value,
        language: data.value.plan.terms.language
    }
})
onServerPrefetch(async () => {
    // Agreements first resolve the lightweight language query; the agreement
    // language boundary then fetches the translated purchase plan for SSR.
    await suspense()
})
</script>
